import { useEffect, useState } from "react";
import { fetchStaticJson, getCachedJson, preloadJson } from "~/lib/json-cache";
import { getOriginalBySlug } from "~/data/ip";

interface StaticDetailState<TItem> {
  data: TItem | null;
  error?: string;
  isLoading: boolean;
}

interface UseStaticDetailOptions<TRaw, TItem> {
  buildPath: (slug: string, id: string) => string;
  extract: (raw: TRaw) => TItem;
  errorMessage: string;
}

export function useStaticDetail<TRaw, TItem>(
  slug: string | undefined,
  id: string | null | undefined,
  options: UseStaticDetailOptions<TRaw, TItem>,
): StaticDetailState<TItem> {
  const path = slug && id && getOriginalBySlug(slug) ? options.buildPath(slug, id) : null;

  const [state, setState] = useState<StaticDetailState<TItem>>(() => {
    if (!path) {
      return { data: null, isLoading: false };
    }

    preloadJson(path);
    const cached = getCachedJson<TRaw>(path);

    if (cached) {
      return { data: options.extract(cached), isLoading: false };
    }

    return { data: null, isLoading: true };
  });

  useEffect(() => {
    if (!path) {
      setState({ data: null, isLoading: false });
      return;
    }

    const cached = getCachedJson<TRaw>(path);

    if (cached) {
      setState({ data: options.extract(cached), isLoading: false });
      return;
    }

    let cancelled = false;

    setState({ data: null, isLoading: true });

    fetchStaticJson<TRaw>(path)
      .then((raw) => {
        if (!cancelled) {
          setState({ data: options.extract(raw), isLoading: false });
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({
            data: null,
            error: error instanceof Error ? error.message : options.errorMessage,
            isLoading: false,
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [path]);

  return state;
}
